import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ScorecardForm from '../components/ScorecardForm';
import AssignmentBar from '../components/AssignmentBar';

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  'http://127.0.0.1:5000';

const tok = () => localStorage.getItem('portalToken');

const MONTH_NAMES = ['january','february','march','april','may','june','july','august','september','october','november','december'];

// Offer letters store the start date as free text (e.g. "1 September 2026"),
// but the contract's commencement date is a real <input type="date">, which
// needs "YYYY-MM-DD" — parse the common "D Month YYYY" shape ourselves since
// native Date parsing of day-first dates is unreliable across browsers.
const parseFreeTextDateToISO = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (match) {
    const day = parseInt(match[1], 10);
    const monthIndex = MONTH_NAMES.indexOf(match[2].toLowerCase());
    const year = parseInt(match[3], 10);
    if (monthIndex !== -1) {
      return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return '';
};

// Default probation period on a fixed-term contract is 3 months.
// Clamps the day to the target month's real length (e.g. 31 Jan + 1 month
// -> 28/29 Feb) instead of letting it roll over into the following month,
// which is what plain Date.setMonth() does.
const addMonthsToISODate = (isoDate, months) => {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T00:00:00`);
  if (isNaN(d.getTime())) return '';
  const targetMonthIndex = d.getMonth() + Number(months);
  const year = d.getFullYear() + Math.floor(targetMonthIndex / 12);
  const month = ((targetMonthIndex % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
  const day = Math.min(d.getDate(), lastDayOfTargetMonth);
  const result = new Date(year, month, day);
  return `${result.getFullYear()}-${String(result.getMonth() + 1).padStart(2, '0')}-${String(result.getDate()).padStart(2, '0')}`;
};
const PROVINCES = ['Eastern Cape','Free State','Gauteng','KwaZulu-Natal','Limpopo','Mpumalanga','North West','Northern Cape','Western Cape'];
const LICENCE_TYPES = ['B','C1','C','EB','EC','EC1'];
const SCRUB_SIZES = ['XS','S','M','L','XL','XXL'];
const DOC_TYPES = [
  { key: 'cv',              label: 'CV / Resume' },
  { key: 'id',              label: 'ID Document' },
  { key: 'drivers_licence', label: "Driver's Licence", hasLicenceFields: true },
  { key: 'hpcsa',           label: 'HPCSA Registration Certificate' },
  { key: 'cpd_certificate', label: 'CPD Compliance Certificate' },
  { key: 'degree',          label: 'Degree' },
  { key: 'salary_slip',     label: 'Salary Slip' },
  { key: 'banking_details', label: 'Banking Details', offerAcceptedOnly: true },
  { key: 'imei_form',       label: 'Crim Check Form', offerAcceptedOnly: true },
];
const REVIEW_STATUS_OPTIONS = [
  'Under review','Short listed','Offered','Offer Letter Sent','Offer Accepted','Placed','Rejected',
];
const STATUS_COLORS = {
  'Under review': 'badge-amber',
  'Short listed': 'badge-blue',
  'Offered': 'badge-gold',
  'Offer Letter Sent': 'badge-gold',
  'Offer Accepted': 'badge-green',
  'Placed': 'badge-green',
  'Rejected': 'badge-red',
};

// 6-stage onboarding checklist — starts once the candidate signs their Offer Letter.
const ONBOARDING_LABELS = {
  team_member_info: 'Awaiting Team Member Info',
  criminal_check: 'Awaiting Criminal Check Consent',
  fixed_term_contract: 'Awaiting Fixed-Term Contract',
  pledge_confidentiality: 'Awaiting Pledge of Confidentiality',
  cellphone_policy: 'Awaiting Cellphone Policy',
  complete: 'Onboarding Complete',
};

// Matches ALL_CHECKS in backend/criminal_check_service.py exactly.
const CRIMINAL_CHECK_OPTIONS = [
  'Credit Check', 'Qualification', 'Employment References', 'Criminal Checks', 'Fraud Check',
  'Passport Verification', 'Legal Right to Work', 'Identity Verifications', 'Insurance Regulations',
  "Driver's license & Vehicles", 'Social Media Screening Checks',
];

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h3 style={{ marginBottom: 16, color: 'var(--accent)' }}>{title}</h3>
      {children}
    </div>
  );
}

function StatusBadge({ done, doneLabel = '✓ Sent', notDoneLabel = 'Not sent' }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
      color: done ? 'var(--green)' : 'var(--text3)',
      background: done ? 'var(--green-bg)' : 'var(--bg4)',
    }}>
      {done ? doneLabel : notDoneLabel}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="form-group">
      <span className="label">{label}</span>
      {children}
    </div>
  );
}

// Simple overlay modal wrapper
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: 28, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CandidateProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // profile edit
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // internal flags
  const [adminNotes, setAdminNotes] = useState('');
  const [isUnemployable, setIsUnemployable] = useState(false);
  const [unemployableReason, setUnemployableReason] = useState('');
  const [rehireEligible, setRehireEligible] = useState('');
  const [rehireNotes, setRehireNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesMsg, setNotesMsg] = useState('');

  // docs
  const [docOpen, setDocOpen] = useState(null);
  const [docForms, setDocForms] = useState({});
  const [docUploading, setDocUploading] = useState({});
  const [docMsg, setDocMsg] = useState({});
  const [docToDelete, setDocToDelete] = useState(null); // { id, filename }

  // refs
  const [refForm, setRefForm] = useState({ name: '', company: '', position: '', phone: '', email: '', relationship: '', admin_note: '' });
  const [refSaving, setRefSaving] = useState(false);
  const [refMsg, setRefMsg] = useState('');

  // compliance
  const [complianceToggling, setComplianceToggling] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testLink, setTestLink] = useState('');
  const [assessMsg, setAssessMsg] = useState('');

  // status save
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // interview recordings
  const [recordings, setRecordings] = useState([]);
  const [recFile, setRecFile] = useState(null);
  const [recTitle, setRecTitle] = useState('');
  const [recUploading, setRecUploading] = useState(false);
  const [recMsg, setRecMsg] = useState('');

  // placement / offer accepted
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [placementForm, setPlacementForm] = useState({ fleet_nr: '', project: '', area: '', manager_name: '', manager_surname: '', manager_contact: '' });
  const [placementSaving, setPlacementSaving] = useState(false);
  const [placementMsg, setPlacementMsg] = useState('');
  const [placementDetail, setPlacementDetail] = useState(null);
  const [imeiFormSent, setImeiFormSent] = useState(false);
  const [orderingScrubs, setOrderingScrubs] = useState(false);
  const [scrubsMsg, setScrubsMsg] = useState('');

  // offer letter
  const [showOfferLetterModal, setShowOfferLetterModal] = useState(false);
  const [offerLetterForm, setOfferLetterForm] = useState({ service_provided: '', location: '', start_date: '', remuneration: '', reporting_to: '', cc_email: '' });
  const [salaryConfirmed, setSalaryConfirmed] = useState(false);
  const [offerLetterSaving, setOfferLetterSaving] = useState(false);
  const [offerLetterMsg, setOfferLetterMsg] = useState('');

  // employment contract
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractForm, setContractForm] = useState({ id_number: '', hpcsa_number: '', residential_address: '', position: '', project_name: '', reporting_manager: '', primary_place_of_work: '', commencement_date: '', fixed_term_end_date: '', probation_months: '3', gross_salary: '', cc_email: '' });
  const [contractConfirmed, setContractConfirmed] = useState(false);
  const [contractSaving, setContractSaving] = useState(false);
  const [contractMsg, setContractMsg] = useState('');

  // contract addendum (extend the existing contract's end date)
  const [showAddendumModal, setShowAddendumModal] = useState(false);
  const [addendumForm, setAddendumForm] = useState({ effective_date: '', extension_months: '', location_of_work: '', reason: '', cc_email: '' });
  const [addendumSaving, setAddendumSaving] = useState(false);
  const [addendumMsg, setAddendumMsg] = useState('');

  // client-side preview only — the backend computes and stores the real value
  const addendumNewEndDatePreview = (() => {
    const months = parseInt(addendumForm.extension_months, 10);
    if (!addendumForm.effective_date || !months || months <= 0) return '';
    const d = new Date(addendumForm.effective_date + 'T00:00:00');
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  })();

  // offer letter + employment contract history (for "done or not" status)
  const [offerLetters, setOfferLetters] = useState([]);
  const [employmentContracts, setEmploymentContracts] = useState([]);

  // criminal check consent
  const [criminalChecks, setCriminalChecks] = useState([]);
  const [showCriminalCheckModal, setShowCriminalCheckModal] = useState(false);
  const [criminalCheckForm, setCriminalCheckForm] = useState({ checks_selected: ['Criminal Checks', 'Identity Verifications'], cc_email: '' });
  const [criminalCheckSaving, setCriminalCheckSaving] = useState(false);
  const [criminalCheckMsg, setCriminalCheckMsg] = useState('');

  // pledge of confidentiality + oath
  const [pledges, setPledges] = useState([]);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [pledgeForm, setPledgeForm] = useState({ cc_email: '' });
  const [pledgeSaving, setPledgeSaving] = useState(false);
  const [pledgeMsg, setPledgeMsg] = useState('');
  const [oathForm, setOathForm] = useState({ oath_answer_1: '', oath_answer_2: '', oath_answer_3: '', oath_certifier_name: '' });
  const [oathSaving, setOathSaving] = useState(false);
  const [oathMsg, setOathMsg] = useState('');

  // cellphone policy + manager approval
  const [cellphonePolicies, setCellphonePolicies] = useState([]);
  const [showCellphoneModal, setShowCellphoneModal] = useState(false);
  const [cellphoneForm, setCellphoneForm] = useState({ cc_email: '' });
  const [cellphoneSaving, setCellphoneSaving] = useState(false);
  const [cellphoneMsg, setCellphoneMsg] = useState('');
  const [needsCellphoneToggling, setNeedsCellphoneToggling] = useState(false);
  const [contractSentSaving, setContractSentSaving] = useState(false);
  const [contractSentMsg, setContractSentMsg] = useState('');
  const [popiaConsentSaving, setPopiaConsentSaving] = useState(false);
  const [popiaConsentMsg, setPopiaConsentMsg] = useState('');
  const [approveForm, setApproveForm] = useState({ manager_approver_name: '' });
  const [approveSaving, setApproveSaving] = useState(false);
  const [approveMsg, setApproveMsg] = useState('');

  const [fleets, setFleets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showNewFleetModal, setShowNewFleetModal] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newFleetForm, setNewFleetForm] = useState({ fleet_nr: '', location_name: '' });
  const [newProjectForm, setNewProjectForm] = useState({ project_name: '' });
  const [loadingMasters, setLoadingMasters] = useState(false);

  // 2nd interview
  const [showSecondInterview, setShowSecondInterview] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const user = JSON.parse(localStorage.getItem('portalUser') || 'null');
  const isAdmin = user?.role === 'admin';
  const isRegionalManager = user?.role === 'regional_manager';
  const isViewer = user?.role === 'viewer'; // legacy support
  const isProjectManager = user?.role === 'project_manager';
  const isUnitManager = user?.role === 'unit_manager';
  const isAccountManager = user?.role === 'account_manager';
  const isFinanceManager = user?.role === 'finance_manager';
  const canSeeSalary = ['admin', 'regional_manager', 'finance_manager'].includes(user?.role);
  const canEdit = ['admin', 'regional_manager'].includes(user?.role);
  const isReadOnly = isProjectManager || isUnitManager || isAccountManager || isFinanceManager;

  const loadRecordings = async () => {
    try {
      const res = await fetch(`${API}/api/recordings/${id}`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setRecordings(d.recordings || []);
    } catch {}
  };

  const loadPlacement = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/placement`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok && d.placement) {
        setPlacementDetail(d.placement);
        setPlacementForm({
          fleet_nr: d.placement.fleet_nr || '',
          project: d.placement.project || '',
          area: d.placement.area || '',
          manager_name: d.placement.manager_name || '',
          manager_surname: d.placement.manager_surname || '',
          manager_contact: d.placement.manager_contact || '',
        });
      }
    } catch {}
  };

  const loadFleets = async () => {
    try {
      const res = await fetch(`${API}/api/admin/fleet`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setFleets(d.fleets || []);
    } catch (e) { console.error('Failed to load fleets:', e); }
  };

  const loadProjects = async () => {
    try {
      const res = await fetch(`${API}/api/admin/projects`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setProjects(d.projects || []);
    } catch (e) { console.error('Failed to load projects:', e); }
  };

  const loadOfferLetters = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/offer-letter`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setOfferLetters(d.offer_letters || []);
    } catch {}
  };

  const loadEmploymentContracts = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/contract`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setEmploymentContracts(d.employment_contracts || []);
    } catch {}
  };

  const loadCriminalChecks = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/criminal-check`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setCriminalChecks(d.criminal_checks || []);
    } catch {}
  };

  const loadPledges = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/pledge-confidentiality`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setPledges(d.pledges || []);
    } catch {}
  };

  const loadCellphonePolicies = async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${id}/cellphone-policy`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.ok) setCellphonePolicies(d.cellphone_policies || []);
    } catch {}
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/admin/applicants/${id}`, { headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json();
      if (res.status === 401 || res.status === 422) {
        throw new Error('Your session has expired — please log out and log back in.');
      }
      if (!res.ok) throw new Error(d.error || d.msg || 'Could not load');
      const a = d.applicant || d;
      setApplicant(a);
      setFields({
        first_name: a.user?.first_name || '', last_name: a.user?.last_name || '',
        email: a.user?.email || '',
        phone: a.phone || '', date_of_birth: a.date_of_birth || '',
        contact_details: a.contact_details || '',
        id_number: a.id_number || '', folder_number: a.folder_number || '',
        ticket_link: a.ticket_link || '',
        contract_ticket_nr: a.contract_ticket_nr || '',
        gender: a.gender || '', position_applied: a.position_applied || '', hpcsa_number: a.hpcsa_number || '',
        years_experience: a.years_experience != null ? String(a.years_experience) : '',
        current_employer: a.current_employer || '', notice_period: a.notice_period || '',
        employment_type: a.employment_type || '', hourly_rate: a.hourly_rate || '',
        salary_expectation: a.salary_expectation || '', address: a.address || '',
        city: a.city || '', province: a.province || '', postal_code: a.postal_code || '',
        availability_status: a.availability_status || 'available',
        available_from: a.available_from || '',
        relocation_preferences: a.relocation_preferences || '',
        application_review_status: a.application_review_status || 'Under review',
        has_drivers_license: a.has_drivers_license || 'no',
        has_degree: a.has_degree || 'no',
        scrub_size: a.scrub_size || '',
        hpcsa_valid_from: a.hpcsa_valid_from || '',
        hpcsa_valid_to: a.hpcsa_valid_to || '',
        cpd_valid_from: a.cpd_valid_from || '',
        cpd_valid_to: a.cpd_valid_to || '',
        imei_valid_from: a.imei_valid_from || '',
        imei_valid_to: a.imei_valid_to || '',
      });
      setAdminNotes(a.admin_notes || '');
      setIsUnemployable(a.is_unemployable || false);
      setUnemployableReason(a.unemployable_reason || '');
      setRehireEligible(a.rehire_eligible || '');
      setRehireNotes(a.rehire_notes || '');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    loadRecordings();
    loadPlacement();
    loadOfferLetters();
    loadEmploymentContracts();
    loadCriminalChecks();
    loadPledges();
    loadCellphonePolicies();
  }, [id]); // eslint-disable-line

  const f = (k) => fields[k] ?? '';
  const setF = (k) => (e) => setFields(p => ({ ...p, [k]: e.target.value }));

  // Extract date of birth from SA ID (first 6 digits: YYMMDD)
  const handleIDChange = (e) => {
    const idValue = e.target.value;
    setFields(p => ({ ...p, id_number: idValue }));

    // If ID has at least 6 digits, parse the date
    const digits = idValue.replace(/\D/g, ''); // Remove non-digits
    if (digits.length >= 6) {
      const yy = digits.substring(0, 2);
      const mm = digits.substring(2, 4);
      const dd = digits.substring(4, 6);

      // Convert YY to YYYY (assume 00-30 = 2000-2030, 31-99 = 1931-1999)
      const year = parseInt(yy) <= 30 ? 2000 + parseInt(yy) : 1900 + parseInt(yy);
      const date = `${year}-${mm}-${dd}`;

      // Validate the date
      const dateObj = new Date(date);
      if (!isNaN(dateObj)) {
        setFields(p => ({ ...p, date_of_birth: date }));
      }
    }
  };

  // Intercept review status change — open placement modal for "Offer Accepted"
  const handleReviewStatusChange = (e) => {
    const val = e.target.value;
    if (val === 'Offer Accepted') {
      setPendingStatus(val);
      loadFleets();
      loadProjects();
      setShowPlacementModal(true);
    } else if (val === 'Short listed') {
      setPendingStatus(val);
      setShowPlacementModal(true);  // Reuse modal to show IMEI form confirmation
    } else {
      setFields(p => ({ ...p, application_review_status: val }));
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true); setSaveMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(fields),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setApplicant(d.applicant || d);
      setSaveMsg('Saved.');
    } catch (e) { setSaveMsg(e.message); }
    finally { setSaving(false); }
  };

  const handleSaveStatus = async () => {
    try {
      setStatusSaving(true); setStatusMsg('');
      const statusVal = fields.application_review_status;
      if (statusVal === 'Offer Accepted') {
        setPendingStatus(statusVal);
        setShowPlacementModal(true);
        setStatusSaving(false);
        return;
      }
      const res = await fetch(`${API}/api/admin/applicants/${id}/review-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ application_review_status: statusVal }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setApplicant(p => ({ ...p, application_review_status: statusVal }));
      setStatusMsg('Status saved.');
    } catch (e) { setStatusMsg(e.message); }
    finally { setStatusSaving(false); }
  };

  const handleSavePlacement = async () => {
    try {
      setPlacementSaving(true); setPlacementMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/placement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(placementForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save placement details');
      setPlacementDetail(d.placement);
      // Now save the status
      const statusRes = await fetch(`${API}/api/admin/applicants/${id}/review-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ application_review_status: pendingStatus }),
      });
      const sd = await statusRes.json();
      if (!statusRes.ok) throw new Error(sd.error || 'Could not update status');
      setFields(p => ({ ...p, application_review_status: pendingStatus }));
      setApplicant(p => ({ ...p, application_review_status: pendingStatus }));
      setShowPlacementModal(false);
      setPendingStatus('');
    } catch (e) { setPlacementMsg(e.message); }
    finally { setPlacementSaving(false); }
  };

  const handleSendOfferLetter = async () => {
    try {
      setOfferLetterSaving(true); setOfferLetterMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/offer-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ ...offerLetterForm, salary_confirmed: salaryConfirmed }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send offer letter');

      // Mirror the existing pipeline stage — matches REVIEW_STATUS_OPTIONS.
      if (fields.application_review_status !== 'Offer Letter Sent') {
        const statusRes = await fetch(`${API}/api/admin/applicants/${id}/review-status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
          body: JSON.stringify({ application_review_status: 'Offer Letter Sent' }),
        });
        if (statusRes.ok) {
          setFields(p => ({ ...p, application_review_status: 'Offer Letter Sent' }));
          setApplicant(p => ({ ...p, application_review_status: 'Offer Letter Sent' }));
        }
      }

      setShowOfferLetterModal(false);
      setOfferLetterForm({ service_provided: '', location: '', start_date: '', remuneration: '', reporting_to: '', cc_email: '' });
      setSalaryConfirmed(false);
      setOfferLetterMsg('');
    } catch (e) {
      setOfferLetterMsg(e.message);
    } finally {
      setOfferLetterSaving(false);
    }
  };

  const openContractModal = () => {
    // Auto-fill from the candidate's profile wherever it's already on file.
    const positionApplied = (applicant?.position_applied || '').toLowerCase();
    const guessedPosition = positionApplied.includes('optometrist')
      ? 'optometrist'
      : (positionApplied.includes('dispenser') || positionApplied.includes('optical')) ? 'dispenser' : '';
    const addressParts = [applicant?.address, applicant?.city, applicant?.province, applicant?.postal_code].filter(Boolean);

    // Also carry over the start date, salary, and CC from the offer letter
    // the candidate already accepted — same job, same terms, so re-typing
    // them here is just a chance to introduce a mismatch. Most recent offer
    // letter first (offerLetters is sorted newest-first by the backend).
    const latestOfferLetter = offerLetters[0];
    const commencementDate = latestOfferLetter ? parseFreeTextDateToISO(latestOfferLetter.start_date) : '';
    const probationEndDate = commencementDate ? addMonthsToISODate(commencementDate, 3) : '';

    setContractForm(p => ({
      ...p,
      id_number: applicant?.id_number || '',
      hpcsa_number: applicant?.hpcsa_number || '',
      residential_address: addressParts.join(', '),
      position: guessedPosition,
      commencement_date: commencementDate || p.commencement_date,
      fixed_term_end_date: probationEndDate || p.fixed_term_end_date,
      gross_salary: latestOfferLetter?.remuneration || p.gross_salary,
      cc_email: latestOfferLetter?.cc_email || p.cc_email,
    }));
    setContractConfirmed(false);
    setContractMsg('');
    setShowContractModal(true);
  };

  const handleSendContract = async () => {
    try {
      setContractSaving(true); setContractMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/contract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(contractForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send employment contract');

      setShowContractModal(false);
      setContractConfirmed(false);
      setContractMsg('');
    } catch (e) {
      setContractMsg(e.message);
    } finally {
      setContractSaving(false);
    }
  };

  const openAddendumModal = () => {
    const latestContract = employmentContracts[0];
    // Pre-fill from the most recent manager "Renew" answer, if any.
    const renewal = (latestContract?.renewal_decisions || []).find(d => d.decision === 'renew');
    setAddendumForm({
      effective_date: renewal?.effective_date || '',
      extension_months: renewal?.extension_months ? String(renewal.extension_months) : '',
      location_of_work: latestContract?.primary_place_of_work || '',
      reason: renewal ? `Renewal requested by ${renewal.manager_name || renewal.manager_email}${renewal.note ? ` — ${renewal.note}` : ''}` : '',
      cc_email: latestContract?.cc_email || '',
    });
    setAddendumMsg('');
    setShowAddendumModal(true);
  };

  const handleSendAddendum = async () => {
    try {
      setAddendumSaving(true); setAddendumMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/contract-addendum`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(addendumForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send contract addendum');

      setShowAddendumModal(false);
      setAddendumMsg('');
      loadEmploymentContracts();
    } catch (e) {
      setAddendumMsg(e.message);
    } finally {
      setAddendumSaving(false);
    }
  };

  const handleSendCriminalCheck = async () => {
    try {
      setCriminalCheckSaving(true); setCriminalCheckMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/criminal-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(criminalCheckForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send criminal check consent');
      setShowCriminalCheckModal(false);
      setCriminalCheckMsg('');
      loadCriminalChecks();
    } catch (e) {
      setCriminalCheckMsg(e.message);
    } finally {
      setCriminalCheckSaving(false);
    }
  };

  const handleSendPledge = async () => {
    try {
      setPledgeSaving(true); setPledgeMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/pledge-confidentiality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(pledgeForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send pledge of confidentiality');
      setShowPledgeModal(false);
      setPledgeMsg('');
      loadPledges();
    } catch (e) {
      setPledgeMsg(e.message);
    } finally {
      setPledgeSaving(false);
    }
  };

  const handleCertifyOath = async (pledgeId) => {
    try {
      setOathSaving(true); setOathMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/pledge-confidentiality/${pledgeId}/oath`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ ...oathForm, oath_certifier_name: oathForm.oath_certifier_name || user?.name || '' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not certify oath');
      setOathMsg('');
      loadPledges();
    } catch (e) {
      setOathMsg(e.message);
    } finally {
      setOathSaving(false);
    }
  };

  const handleSendCellphone = async () => {
    try {
      setCellphoneSaving(true); setCellphoneMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/cellphone-policy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(cellphoneForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send cellphone policy');
      setShowCellphoneModal(false);
      setCellphoneMsg('');
      loadCellphonePolicies();
    } catch (e) {
      setCellphoneMsg(e.message);
    } finally {
      setCellphoneSaving(false);
    }
  };

  const handleApproveCellphone = async (policyId) => {
    try {
      setApproveSaving(true); setApproveMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/cellphone-policy/${policyId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ ...approveForm, manager_approver_name: approveForm.manager_approver_name || user?.name || '' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not approve cellphone policy');
      setApproveMsg('');
      loadCellphonePolicies();
    } catch (e) {
      setApproveMsg(e.message);
    } finally {
      setApproveSaving(false);
    }
  };

  const [letsHireSending, setLetsHireSending] = useState(false);
  const [letsHireMsg, setLetsHireMsg] = useState('');
  const handleLetsHire = async () => {
    const prior = applicant.hire_requests?.[0];
    const name = applicant.user?.first_name || 'this candidate';
    const question = prior
      ? `A hire request was already sent on ${new Date(prior.created_at).toLocaleDateString()}.\n\nSend another one for ${name}?`
      : `Let admin/HR know you want to hire ${name}?`;
    if (!window.confirm(question)) return;
    try {
      setLetsHireSending(true); setLetsHireMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/lets-hire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
      });
      const d = await res.json();
      if (res.ok) {
        setLetsHireMsg('✓ Admin/HR notified');
        const rmName = `${user?.name || 'You'}`;
        setApplicant(p => ({
          ...p,
          hire_requests: [{ message: `${rmName} wants to hire ${p.user?.first_name || ''} ${p.user?.last_name || ''}.`.trim(), created_at: new Date().toISOString() }, ...(p.hire_requests || [])],
        }));
      } else {
        setLetsHireMsg(d.error || 'Could not send request');
      }
    } catch (e) {
      setLetsHireMsg(e.message);
    } finally {
      setLetsHireSending(false);
    }
  };

  const handleToggleNeedsCellphone = async (checked) => {
    try {
      setNeedsCellphoneToggling(true);
      const res = await fetch(`${API}/api/admin/applicants/${id}/needs-cellphone-policy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ needs_cellphone_policy: checked }),
      });
      const d = await res.json();
      if (res.ok) {
        setApplicant(p => ({ ...p, needs_cellphone_policy: d.needs_cellphone_policy, onboarding_stage: d.onboarding_stage }));
      }
    } catch {}
    finally {
      setNeedsCellphoneToggling(false);
    }
  };

  const handleToggleContractSent = async (checked) => {
    try {
      setContractSentSaving(true); setContractSentMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ contract_sent: checked, contract_ticket_nr: fields.contract_ticket_nr }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setApplicant(p => ({ ...p, contract_sent: checked, contract_ticket_nr: fields.contract_ticket_nr }));
      setContractSentMsg('Saved.');
    } catch (e) {
      setContractSentMsg(e.message || 'Could not save');
    } finally {
      setContractSentSaving(false);
    }
  };

  // Manual "done outside Talent Pool" ticks for the onboarding items —
  // for staff who signed these on paper before being loaded.
  const [doneSaving, setDoneSaving] = useState('');
  const [doneMsg, setDoneMsg] = useState({});
  const handleToggleDone = async (field, checked) => {
    try {
      setDoneSaving(field); setDoneMsg(m => ({ ...m, [field]: '' }));
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ [field]: checked }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setApplicant(p => ({ ...p, [field]: checked }));
      setDoneMsg(m => ({ ...m, [field]: 'Saved.' }));
    } catch (e) {
      setDoneMsg(m => ({ ...m, [field]: e.message || 'Could not save' }));
    } finally {
      setDoneSaving('');
    }
  };
  const doneTick = (field) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 10, cursor: 'pointer' }}>
      <input type="checkbox" style={{ width: 'auto' }} checked={!!applicant[field]}
        disabled={doneSaving === field} onChange={e => handleToggleDone(field, e.target.checked)} />
      Done outside Talent Pool (e.g. signed on paper)
      {doneMsg[field] && (
        <span style={{ fontSize: 12, color: doneMsg[field] === 'Saved.' ? 'var(--green)' : 'var(--red)' }}>{doneMsg[field]}</span>
      )}
    </label>
  );

  const handleTogglePopiaConsent = async (checked) => {
    try {
      setPopiaConsentSaving(true); setPopiaConsentMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ popia_consent: checked }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setApplicant(p => ({ ...p, popia_consent: d.applicant?.popia_consent, popia_consent_at: d.applicant?.popia_consent_at }));
      setPopiaConsentMsg('Saved.');
    } catch (e) {
      setPopiaConsentMsg(e.message || 'Could not save');
    } finally {
      setPopiaConsentSaving(false);
    }
  };

  const handleOrderScrubs = async () => {
    try {
      setOrderingScrubs(true); setScrubsMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/order-scrubs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not send order');
      setScrubsMsg(d.message || 'Purchase order sent.');
    } catch (e) { setScrubsMsg(e.message); }
    finally { setOrderingScrubs(false); }
  };

  const handleSaveNotes = async () => {
    try {
      setNotesSaving(true); setNotesMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({
          admin_notes: adminNotes, is_unemployable: isUnemployable, unemployable_reason: unemployableReason,
          rehire_eligible: rehireEligible, rehire_notes: rehireNotes,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setNotesMsg('Notes saved.');
    } catch (e) { setNotesMsg(e.message); }
    finally { setNotesSaving(false); }
  };

  const getDocForm = (type) => docForms[type] || {};
  const setDocField = (type, key, val) => setDocForms(p => ({ ...p, [type]: { ...getDocForm(type), [key]: val } }));

  const handleDocUpload = async (type) => {
    const form = getDocForm(type);
    if (!form.file) { setDocMsg(p => ({ ...p, [type]: 'Select a file first.' })); return; }
    try {
      setDocUploading(p => ({ ...p, [type]: true })); setDocMsg(p => ({ ...p, [type]: '' }));
      const fd = new FormData();
      fd.append('file', form.file); fd.append('document_type', type);
      if (form.notes) fd.append('notes', form.notes);
      if (form.section_comment) fd.append('section_comment', form.section_comment);
      if (type === 'drivers_licence') {
        if (form.licence_type) fd.append('licence_type', form.licence_type);
        if (form.licence_valid_from) fd.append('licence_valid_from', form.licence_valid_from);
        if (form.licence_valid_to) fd.append('licence_valid_to', form.licence_valid_to);
        if (form.licence_first_issue_date) fd.append('licence_first_issue_date', form.licence_first_issue_date);
      }
      const res = await fetch(`${API}/api/documents/admin/upload/${id}`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok()}` }, body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Upload failed');
      setDocMsg(p => ({ ...p, [type]: 'Uploaded.' }));
      setDocForms(p => ({ ...p, [type]: {} }));
      load();
    } catch (e) { setDocMsg(p => ({ ...p, [type]: e.message })); }
    finally { setDocUploading(p => ({ ...p, [type]: false })); }
  };

  const handleDocDelete = async () => {
    if (!docToDelete) return;
    try {
      const res = await fetch(`${API}/api/documents/${docToDelete.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Could not delete'); }
      setDocToDelete(null);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleUploadRecording = async () => {
    if (!recFile) { setRecMsg('Select a file first.'); return; }
    try {
      setRecUploading(true); setRecMsg('');
      const fd = new FormData();
      fd.append('file', recFile);
      if (recTitle.trim()) fd.append('title', recTitle.trim());
      const res = await fetch(`${API}/api/recordings/${id}`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok()}` }, body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Upload failed');
      setRecMsg('Uploaded.');
      setRecFile(null); setRecTitle('');
      loadRecordings();
    } catch (e) { setRecMsg(e.message); }
    finally { setRecUploading(false); }
  };

  const handleDeleteRecording = async (recId) => {
    const res = await fetch(`${API}/api/recordings/${recId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` },
    });
    if (res.ok) loadRecordings();
  };

  const handleAddRef = async () => {
    if (!refForm.name.trim()) { setRefMsg('Name is required.'); return; }
    try {
      setRefSaving(true); setRefMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/references`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(refForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not add');
      setRefMsg('Reference added.');
      setRefForm({ name: '', company: '', position: '', phone: '', email: '', relationship: '', admin_note: '' });
      load();
    } catch (e) { setRefMsg(e.message); }
    finally { setRefSaving(false); }
  };

  const handleDeleteRef = async (refId) => {
    const res = await fetch(`${API}/api/admin/applicants/${id}/references/${refId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` },
    });
    if (res.ok) load();
  };

  const [deletingCandidate, setDeletingCandidate] = useState(false);
  const handleDeleteCandidate = async () => {
    const fullName = `${applicant?.user?.first_name || ''} ${applicant?.user?.last_name || ''}`.trim() || 'this candidate';
    if (!window.confirm(`Permanently delete ${fullName}? This removes their profile, documents, comments and test scores. Their login will be switched off. This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingCandidate(true);
      const res = await fetch(`${API}/api/admin/applicants/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || d.msg || 'Could not delete candidate');
      navigate('/');
    } catch (e) {
      alert(e.message);
    } finally {
      setDeletingCandidate(false);
    }
  };

  const handleToggleCompliance = async () => {
    try {
      setComplianceToggling(true); setAssessMsg('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/compliance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ compliance_complete: !applicant.compliance_complete }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      setApplicant(p => ({ ...p, compliance_complete: d.compliance_complete, compliance_completed_at: d.compliance_completed_at }));
      setAssessMsg(d.compliance_complete ? 'Compliance marked complete.' : 'Marked incomplete.');
    } catch (e) { setAssessMsg(e.message); }
    finally { setComplianceToggling(false); }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true); setAssessMsg(''); setTestLink('');
      const res = await fetch(`${API}/api/admin/applicants/${id}/test/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      const link = `${window.location.origin}/test/${d.token}`;
      setTestLink(link);
      setAssessMsg('Test link generated.');
      load();
    } catch (e) { setAssessMsg(e.message); }
    finally { setSendingTest(false); }
  };

  // ── render ──

  if (!user) return <p style={{ padding: 24, color: 'var(--text2)' }}>Please log in via the Admin portal.</p>;

  if (loading) return <p style={{ padding: 24, color: 'var(--text2)' }}>Loading…</p>;
  if (error)   return <p style={{ padding: 24, color: 'var(--red)' }}>{error}</p>;
  if (!applicant) return null;

  const iStyle = { width: '100%' };
  const gridTwo = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

  const showOrderScrubs = applicant.application_review_status === 'Offer Accepted' && placementDetail;

  return (
    <div>
      {/* back button */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{ marginBottom: 14, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 14, padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        ← Back
      </button>

      {/* breadcrumb */}
      <div style={{ marginBottom: 20, fontSize: 14, color: 'var(--text2)' }}>
        <Link to="/">Talent Pool</Link>
        <span style={{ margin: '0 8px' }}>›</span>
        <span style={{ color: 'var(--text)' }}>{applicant.user?.first_name} {applicant.user?.last_name}</span>
      </div>

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginBottom: 6 }}>{applicant.user?.first_name} {applicant.user?.last_name}</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {applicant.position_applied && <span className="badge badge-gold">{applicant.position_applied}</span>}
            {applicant.application_review_status && (
              <span className={`badge ${STATUS_COLORS[applicant.application_review_status] || 'badge-gray'}`}>
                {applicant.application_review_status}
              </span>
            )}
            {applicant.is_unemployable && isAdmin && <span className="badge badge-red">⛔ Unemployable</span>}
            {applicant.onboarding_stage && (
              <span className="badge badge-blue">Onboarding: {ONBOARDING_LABELS[applicant.onboarding_stage] || applicant.onboarding_stage}</span>
            )}
          </div>
          {applicant.hire_requests?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text2)' }}>
              🤝 <strong>Hire requested</strong> — {applicant.hire_requests[0].message}{' '}
              <span style={{ color: 'var(--text3)' }}>
                ({new Date(applicant.hire_requests[0].created_at).toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {applicant.hire_requests.length > 1 ? ` · ${applicant.hire_requests.length} requests` : ''})
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEmailCopied(false); setShowSecondInterview(true); }}>
            Request 2nd Interview
          </button>
          {(isRegionalManager || isAdmin) && applicant.application_review_status === 'Short listed' && (
            <>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleLetsHire} disabled={letsHireSending}>
                {letsHireSending ? 'Sending…' : "Let's hire"}
              </button>
              {letsHireMsg && <span style={{ fontSize: 12, color: 'var(--text2)' }}>{letsHireMsg}</span>}
            </>
          )}
          {applicant.latest_test?.status === 'submitted' && (
            <Link to={`/candidate/${id}/mark-test`} className="btn btn-primary btn-sm">Mark Test →</Link>
          )}
          {isAdmin && (
            <button type="button" className="btn btn-danger btn-sm" onClick={handleDeleteCandidate} disabled={deletingCandidate}>
              {deletingCandidate ? 'Deleting…' : 'Delete Candidate'}
            </button>
          )}
        </div>
      </div>

      {/* ── Profile ── */}
      {/* Assigned-to bar: hand this profile to a staff member with a note */}
      <AssignmentBar applicantId={id} />

      <Section title="Profile Details">
        <div style={gridTwo}>
          <Field label="First Name"><input style={iStyle} value={f('first_name')} onChange={setF('first_name')} /></Field>
          <Field label="Last Name"><input style={iStyle} value={f('last_name')} onChange={setF('last_name')} /></Field>
          <Field label="Phone"><input style={iStyle} value={f('phone')} onChange={setF('phone')} /></Field>
          <Field label="Email Address"><input style={iStyle} value={f('email')} onChange={setF('email')} /></Field>
          <Field label="Contact Details"><input style={iStyle} value={f('contact_details')} onChange={setF('contact_details')} placeholder="Additional contact info" /></Field>
          <Field label="Date of Birth"><input type="date" style={iStyle} value={f('date_of_birth')} onChange={setF('date_of_birth')} /></Field>
          <Field label="SA ID / Passport"><input style={iStyle} value={f('id_number')} onChange={handleIDChange} placeholder="e.g. 900501 0005555" /></Field>
          <Field label="Gender">
            <select style={iStyle} value={f('gender')} onChange={setF('gender')}>
              <option value="">— Select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Ticket Nr"><input style={iStyle} value={f('folder_number')} onChange={setF('folder_number')} /></Field>
          <Field label="Ticket Link"><input style={iStyle} value={f('ticket_link')} onChange={setF('ticket_link')} placeholder="https://..." /></Field>
          <Field label="Position Applied">
            <select style={iStyle} value={f('position_applied')} onChange={setF('position_applied')}>
              <option value="">— Select —</option>
              <option>Optometrist</option>
              <option>Optical Dispenser</option>
            </select>
          </Field>
          <Field label="HPCSA Number"><input style={iStyle} value={f('hpcsa_number')} onChange={setF('hpcsa_number')} /></Field>
          <Field label="Years of Experience"><input type="number" min="0" style={iStyle} value={f('years_experience')} onChange={setF('years_experience')} /></Field>
          <Field label="Employment Type">
            <select style={iStyle} value={f('employment_type')} onChange={setF('employment_type')}>
              <option value="">— Select —</option>
              <option value="permanent">Permanent Employment</option>
              <option value="fixed_term">Fixed Term</option>
              <option value="locum">Locum</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Current Employer"><input style={iStyle} value={f('current_employer')} onChange={setF('current_employer')} placeholder={f('employment_type') === 'locum' ? 'Locum / agency' : ''} /></Field>
          <Field label="Notice Period"><input style={iStyle} value={f('notice_period')} onChange={setF('notice_period')} placeholder="e.g. 1 month" /></Field>
          {canSeeSalary ? (
            f('employment_type') === 'locum' ? (
              <Field label="Rate per Hour"><input style={iStyle} value={f('hourly_rate')} onChange={setF('hourly_rate')} placeholder="e.g. R250/hr" /></Field>
            ) : (
              <Field label="Salary Expectation"><input style={iStyle} value={f('salary_expectation')} onChange={setF('salary_expectation')} placeholder="e.g. R25 000 pm" /></Field>
            )
          ) : (
            <Field label={f('employment_type') === 'locum' ? 'Rate per Hour' : 'Salary Expectation'}><div style={{ padding: '8px 10px', background: '#fff3e0', borderRadius: 6, fontSize: 13, color: '#e65100', border: '1px solid #ffcc80' }}>🔒 Hidden — not visible to your role</div></Field>
          )}
          <Field label="Driver's License">
            <select style={iStyle} value={f('has_drivers_license')} onChange={setF('has_drivers_license')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="Has a copy of Degree / Qualification">
            <select style={iStyle} value={f('has_degree')} onChange={setF('has_degree')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="Scrub Size">
            <select style={iStyle} value={f('scrub_size')} onChange={setF('scrub_size')}>
              <option value="">— Select —</option>
              {SCRUB_SIZES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Street Address"><input style={iStyle} value={f('address')} onChange={setF('address')} /></Field>
          <Field label="City / Suburb"><input style={iStyle} value={f('city')} onChange={setF('city')} /></Field>
          <Field label="Province">
            <select style={iStyle} value={f('province')} onChange={setF('province')}>
              <option value="">— Select —</option>
              {PROVINCES.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Postal Code"><input style={iStyle} value={f('postal_code')} onChange={setF('postal_code')} /></Field>
          <Field label="Availability">
            <select style={iStyle} value={f('availability_status')} onChange={setF('availability_status')}>
              <option value="available">Available</option>
              <option value="open_to_offers">Open to offers</option>
              <option value="on_project">On project</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </Field>
          <Field label="Available From"><input type="date" style={iStyle} value={f('available_from')} onChange={setF('available_from')} /></Field>
          <Field label="Review Status">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select style={{ flex: 1 }} value={f('application_review_status')} onChange={handleReviewStatusChange}>
                {REVIEW_STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveStatus} disabled={statusSaving} style={{ whiteSpace: 'nowrap' }}>
                {statusSaving ? 'Saving…' : 'Save Status'}
              </button>
            </div>
            {statusMsg && <p style={{ margin: '4px 0 0', fontSize: 12, color: statusMsg === 'Status saved.' ? 'var(--green)' : 'var(--red)' }}>{statusMsg}</p>}
          </Field>
        </div>
        <Field label="Relocation Preferences (comma-separated)">
          <input style={iStyle} value={f('relocation_preferences')} onChange={setF('relocation_preferences')} placeholder="Gauteng, Western Cape" />
        </Field>
        {saveMsg && <p style={{ color: saveMsg === 'Saved.' ? 'var(--green)' : 'var(--red)', marginBottom: 10, fontSize: 13 }}>{saveMsg}</p>}
        {!isReadOnly && <button type="button" className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
          {saving ? 'Saving…' : 'Save Profile Changes'}
        </button>}

        {/* POPIA Consent — visible from the start, not gated behind onboarding
            stage, since it should already be in place before the candidate
            was ever added to the system. */}
        <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
            🔒 POPIA Consent
            <StatusBadge done={!!applicant.popia_consent} doneLabel="✓ Recorded" notDoneLabel="Not recorded" />
          </p>
          {applicant.popia_consent && applicant.popia_consent_at && (
            <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text3)' }}>
              Recorded {new Date(applicant.popia_consent_at).toLocaleDateString()}
            </p>
          )}
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: popiaConsentSaving ? 'default' : 'pointer' }}>
              <input
                type="checkbox"
                checked={!!applicant.popia_consent}
                disabled={popiaConsentSaving}
                onChange={(e) => handleTogglePopiaConsent(e.target.checked)}
              />
              Consent confirmed (candidate agreed to Guud Vision storing, processing and contacting them, including by email, before or when this profile was created)
            </label>
          )}
          {popiaConsentMsg && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: popiaConsentMsg === 'Saved.' ? 'var(--green)' : 'var(--red)' }}>
              {popiaConsentMsg}
            </p>
          )}
        </div>

        {/* Generate Offer Letter — admin only, once shortlisted or further along */}
        {isAdmin && fields.application_review_status && !['Under review', 'Rejected'].includes(fields.application_review_status) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              📄 Offer Letter
              <StatusBadge done={offerLetters.length > 0 || !!applicant.offer_letter_done} />
            </p>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowOfferLetterModal(true)}>
              Generate Offer Letter
            </button>
            {doneTick('offer_letter_done')}
          </div>
        )}

        {/* Generate Employment Contract — admin only, once shortlisted or further along */}
        {isAdmin && fields.application_review_status && !['Under review', 'Rejected'].includes(fields.application_review_status) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              📝 Employment Contract
              <StatusBadge done={employmentContracts.length > 0 || !!applicant.contract_sent} />
            </p>
            <button type="button" className="btn btn-primary btn-sm" onClick={openContractModal}>
              Generate Contract
            </button>
            {employmentContracts.length > 0 && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={openAddendumModal} style={{ marginLeft: 8 }}>
                Extend Contract
              </button>
            )}

            {/* Managers' Renew / Don't renew answers from their contract
                expiry emails — requests only; Extend Contract pre-fills
                from the latest Renew. */}
            {(employmentContracts[0]?.renewal_decisions || []).map((d, i) => (
              <p key={i} style={{ margin: '10px 0 0', fontSize: 13, padding: '8px 10px', borderRadius: 6,
                background: d.decision === 'renew' ? '#eaf6ec' : '#fbeaea' }}>
                <b>{d.manager_name || d.manager_email}</b>{' '}
                {d.decision === 'renew'
                  ? <>asked to <b>renew for {d.extension_months} months</b> from {d.effective_date}</>
                  : <>asked <b>not to renew</b></>}
                {' '}<span style={{ color: 'var(--text3, #888)' }}>({d.decided_at?.slice(0, 10)})</span>
                {d.note && <><br /><i>“{d.note}”</i></>}
              </p>
            ))}

            {/* Manual "contract sent" marker + ticket number — separate from
                the Generate Contract flow above, for recording a contract
                that was sent outside the app (or just confirming it went
                out), with a reference/ticket number for the request. */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border2)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: contractSentSaving ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={!!applicant.contract_sent}
                  disabled={contractSentSaving}
                  onChange={(e) => handleToggleContractSent(e.target.checked)}
                />
                Contract sent
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input
                  style={{ ...iStyle, flex: 1 }}
                  placeholder="Ticket number"
                  value={f('contract_ticket_nr')}
                  onChange={setF('contract_ticket_nr')}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={contractSentSaving}
                  onClick={() => handleToggleContractSent(!!applicant.contract_sent)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {contractSentSaving ? 'Saving…' : 'Save Ticket Nr'}
                </button>
              </div>
              {contractSentMsg && (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: contractSentMsg === 'Saved.' ? 'var(--green)' : 'var(--red)' }}>
                  {contractSentMsg}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Criminal Check Consent — admin only, once shortlisted or further along */}
        {isAdmin && fields.application_review_status && !['Under review', 'Rejected'].includes(fields.application_review_status) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              🕵️ Criminal Check Consent
              <StatusBadge done={criminalChecks.length > 0 || !!applicant.criminal_check_done} />
            </p>
            {/* Placed candidates can still get a criminal check consent sent
                (e.g. a re-check) — only block it for candidates genuinely
                earlier in onboarding than this step. */}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowCriminalCheckModal(true)}
              disabled={applicant.onboarding_stage !== 'criminal_check' && fields.application_review_status !== 'Placed'}
            >
              Send Criminal Check Consent
            </button>
            {applicant.onboarding_stage !== 'criminal_check' && fields.application_review_status !== 'Placed' && (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text3)' }}>
                {!applicant.onboarding_stage
                  ? 'Available once the candidate has signed their Offer Letter.'
                  : applicant.onboarding_stage === 'team_member_info'
                  ? 'Complete Team Member Info first.'
                  : 'This onboarding step has already been passed.'}
              </p>
            )}
            {doneTick('criminal_check_done')}
            {criminalChecks.map(cc => (
              <div key={cc.id} style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg4)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                <div>Sent {new Date(cc.sent_at).toLocaleDateString('en-ZA')}{cc.cc_email ? ` · CC: ${cc.cc_email}` : ''}</div>
                {cc.signed_at ? (
                  <>
                    <div style={{ color: 'var(--green)', fontWeight: 600, marginTop: 4 }}>✅ Signed {new Date(cc.signed_at).toLocaleDateString('en-ZA')}</div>
                    {cc.previous_convictions && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--amber-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--amber)' }}>
                        ⚠ Previous convictions declared: {cc.conviction_details || '—'}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: 'var(--text3)', marginTop: 4 }}>⏳ Awaiting candidate signature</div>
                )}
              </div>
            ))}
            {criminalCheckMsg && <p style={{ marginTop: 8, fontSize: 13, color: 'var(--red)' }}>{criminalCheckMsg}</p>}
          </div>
        )}

        {/* Pledge of Confidentiality + Oath — admin only, once shortlisted or further along */}
        {isAdmin && fields.application_review_status && !['Under review', 'Rejected'].includes(fields.application_review_status) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              🤐 Pledge of Confidentiality
              <StatusBadge done={pledges.length > 0 || !!applicant.pledge_done} />
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowPledgeModal(true)}
              disabled={applicant.onboarding_stage !== 'pledge_confidentiality' && fields.application_review_status !== 'Placed'}
            >
              Send Pledge of Confidentiality
            </button>
            {applicant.onboarding_stage !== 'pledge_confidentiality' && fields.application_review_status !== 'Placed' && (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text3)' }}>
                Available once the candidate reaches the Pledge of Confidentiality onboarding step.
              </p>
            )}
            {doneTick('pledge_done')}
            {pledges.map(p => (
              <div key={p.id} style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg4)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                <div>Sent {new Date(p.sent_at).toLocaleDateString('en-ZA')}{p.cc_email ? ` · CC: ${p.cc_email}` : ''}</div>
                {p.signed_at ? (
                  <div style={{ color: 'var(--green)', fontWeight: 600, marginTop: 4 }}>✅ Signed {new Date(p.signed_at).toLocaleDateString('en-ZA')}</div>
                ) : (
                  <div style={{ color: 'var(--text3)', marginTop: 4 }}>⏳ Awaiting candidate signature</div>
                )}
                {p.signed_at && p.oath_certified_at && (
                  <div style={{ color: 'var(--green)', marginTop: 4 }}>✅ Oath certified by {p.oath_certifier_name || '—'} on {new Date(p.oath_certified_at).toLocaleDateString('en-ZA')}</div>
                )}
                {p.signed_at && !p.oath_certified_at && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600 }}>Complete the Oath</p>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <Field label="Oath Answer 1"><textarea rows={2} value={oathForm.oath_answer_1} onChange={e => setOathForm(f => ({ ...f, oath_answer_1: e.target.value }))} /></Field>
                      <Field label="Oath Answer 2"><textarea rows={2} value={oathForm.oath_answer_2} onChange={e => setOathForm(f => ({ ...f, oath_answer_2: e.target.value }))} /></Field>
                      <Field label="Oath Answer 3"><textarea rows={2} value={oathForm.oath_answer_3} onChange={e => setOathForm(f => ({ ...f, oath_answer_3: e.target.value }))} /></Field>
                      <Field label="Certifier Name"><input value={oathForm.oath_certifier_name || user?.name || ''} onChange={e => setOathForm(f => ({ ...f, oath_certifier_name: e.target.value }))} /></Field>
                    </div>
                    {oathMsg && <p style={{ marginTop: 6, fontSize: 12, color: 'var(--red)' }}>{oathMsg}</p>}
                    <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={() => handleCertifyOath(p.id)} disabled={oathSaving}>
                      {oathSaving ? 'Certifying…' : 'Certify Oath'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Cellphone Policy + Manager Approval — admin only, once shortlisted or further along */}
        {isAdmin && fields.application_review_status && !['Under review', 'Rejected'].includes(fields.application_review_status) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              📱 Cellphone Policy
              {applicant.needs_cellphone_policy !== false && (
                <StatusBadge done={cellphonePolicies.length > 0 || !!applicant.cellphone_policy_done} />
              )}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={applicant.needs_cellphone_policy !== false}
                onChange={e => handleToggleNeedsCellphone(e.target.checked)}
                disabled={needsCellphoneToggling}
              />
              Issue this candidate a company cell phone?
            </label>
            {applicant.needs_cellphone_policy === false ? (
              <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>Not applicable — no company phone issued.</p>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowCellphoneModal(true)}
                  disabled={applicant.onboarding_stage !== 'cellphone_policy' && fields.application_review_status !== 'Placed'}
                >
                  Send Cellphone Policy
                </button>
                {applicant.onboarding_stage !== 'cellphone_policy' && fields.application_review_status !== 'Placed' && (
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text3)' }}>
                    Available once the candidate reaches the Cellphone Policy onboarding step.
                  </p>
                )}
                {doneTick('cellphone_policy_done')}
                {cellphonePolicies.map(cp => (
                  <div key={cp.id} style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg4)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                    <div>Sent {new Date(cp.sent_at).toLocaleDateString('en-ZA')}{cp.cc_email ? ` · CC: ${cp.cc_email}` : ''}</div>
                    {cp.signed_at ? (
                      <div style={{ color: 'var(--green)', fontWeight: 600, marginTop: 4 }}>✅ Signed {new Date(cp.signed_at).toLocaleDateString('en-ZA')}</div>
                    ) : (
                      <div style={{ color: 'var(--text3)', marginTop: 4 }}>⏳ Awaiting candidate signature</div>
                    )}
                    {cp.signed_at && cp.manager_approved_at && (
                      <div style={{ color: 'var(--green)', marginTop: 4 }}>✅ Approved by {cp.manager_approver_name || '—'} on {new Date(cp.manager_approved_at).toLocaleDateString('en-ZA')}</div>
                    )}
                    {cp.signed_at && !cp.manager_approved_at && (
                      <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)' }}>
                        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600 }}>Manager Approval</p>
                        <Field label="Approver Name">
                          <input value={approveForm.manager_approver_name || user?.name || ''} onChange={e => setApproveForm({ manager_approver_name: e.target.value })} />
                        </Field>
                        {approveMsg && <p style={{ marginTop: 6, fontSize: 12, color: 'var(--red)' }}>{approveMsg}</p>}
                        <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 4 }} onClick={() => handleApproveCellphone(cp.id)} disabled={approveSaving}>
                          {approveSaving ? 'Approving…' : 'Approve'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Order Scrubs button — shown when Offer Accepted and placement details saved */}
        {showOrderScrubs && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--green-bg)', border: '1px solid rgba(76,175,133,0.3)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 500, color: 'var(--green)' }}>
              ✓ Offer Accepted — placement details saved.
            </p>
            {scrubsMsg && <p style={{ fontSize: 13, marginBottom: 8, color: scrubsMsg.includes('sent') ? 'var(--green)' : 'var(--red)' }}>{scrubsMsg}</p>}
            <button type="button" className="btn btn-success btn-sm" onClick={handleOrderScrubs} disabled={orderingScrubs}>
              {orderingScrubs ? 'Sending…' : '🧾 Order Scrubs'}
            </button>
          </div>
        )}
      </Section>

      {/* ── Internal Flags (admin only) ── */}
      {isAdmin && (
        <Section title="Internal Flags & Admin Notes">
          <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 'var(--radius-sm)', border: `1px solid ${isUnemployable ? 'rgba(224,92,92,0.3)' : 'var(--border2)'}`, background: isUnemployable ? 'var(--red-bg)' : 'var(--bg3)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: isUnemployable ? 12 : 0 }}>
              <input type="checkbox" checked={isUnemployable} onChange={e => setIsUnemployable(e.target.checked)} style={{ width: 'auto' }} />
              <span style={{ fontWeight: 500, color: isUnemployable ? 'var(--red)' : 'var(--text)' }}>
                {isUnemployable ? '⛔ Marked as Unemployable' : 'Mark as Unemployable'}
              </span>
            </label>
            {isUnemployable && (
              <Field label="Reason (internal only)">
                <textarea value={unemployableReason} onChange={e => setUnemployableReason(e.target.value)} rows={2} />
              </Field>
            )}
          </div>
          <Field label="Admin Notes">
            <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={4} placeholder="Interview feedback, flags…" />
          </Field>
          <Field label="Rehire Eligible — would Guud Vision employ them again?">
            <select value={rehireEligible} onChange={e => setRehireEligible(e.target.value)}>
              <option value="">Not recorded</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unsure">Unsure</option>
            </select>
          </Field>
          <Field label="Rehire Notes">
            <textarea value={rehireNotes} onChange={e => setRehireNotes(e.target.value)} rows={2} placeholder="e.g. great worker, left on good terms…" />
          </Field>
          {notesMsg && <p style={{ color: notesMsg === 'Notes saved.' ? 'var(--green)' : 'var(--red)', marginBottom: 10, fontSize: 13 }}>{notesMsg}</p>}
          <button type="button" className="btn btn-ghost" onClick={handleSaveNotes} disabled={notesSaving}>
            {notesSaving ? 'Saving…' : 'Save Notes'}
          </button>
        </Section>
      )}

      {/* ── Documents ── */}
      <Section title="Documents">
        {DOC_TYPES.filter(docType => {
          if (docType.offerAcceptedOnly) return applicant.application_review_status === 'Offer Accepted';
          return true;
        }).map(docType => {
          const existing = (applicant.documents || []).filter(d => d.document_type === docType.key);
          const form = getDocForm(docType.key);
          const isOpen = docOpen === docType.key;
          return (
            <div key={docType.key} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8, overflow: 'hidden' }}>
              <button type="button" onClick={() => setDocOpen(isOpen ? null : docType.key)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: isOpen ? 'var(--bg3)' : 'var(--bg4)', border: 'none', cursor: 'pointer', color: 'var(--text)', fontFamily: 'var(--font)', fontWeight: 500 }}>
                <span>
                  <span style={{ marginRight: 10 }}>{existing.length > 0 ? <span style={{ color: 'var(--green)' }}>✓</span> : <span style={{ color: 'var(--red)' }}>✗</span>}</span>
                  {docType.label}
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text3)' }}>{existing.length > 0 ? `${existing.length} file${existing.length > 1 ? 's' : ''}` : 'awaiting upload'}</span>
                </span>
                <span style={{ color: 'var(--text3)' }}>{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
                  {existing.map(doc => (
                    <div key={doc.id} style={{ fontSize: 13, padding: '6px 10px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span>📄 {doc.filename}</span>
                          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          <button
                            type="button"
                            style={{ fontSize: 12, cursor: 'pointer', color: 'var(--accent)', background: 'none', border: 'none', padding: 0 }}
                            onClick={async () => {
                              const res = await fetch(`${API}/api/documents/${doc.id}/view`, { headers: { Authorization: `Bearer ${tok()}` } });
                              const blob = await res.blob();
                              window.open(URL.createObjectURL(blob), '_blank');
                            }}
                          >View</button>
                          <a href={`${API}/api/documents/${doc.id}/download`}
                            onClick={async e => { e.preventDefault(); const res = await fetch(`${API}/api/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${tok()}` } }); const blob = await res.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = doc.filename; a.click(); }}
                            style={{ fontSize: 12 }}>Download</a>
                          {(isAdmin || isRegionalManager || isViewer) && (
                            <button
                              type="button"
                              style={{ fontSize: 12, cursor: 'pointer', color: 'var(--red)', background: 'none', border: 'none', padding: 0 }}
                              onClick={() => setDocToDelete({ id: doc.id, filename: doc.filename })}
                            >Remove</button>
                          )}
                        </div>
                      </div>
                      {doc.licence_type && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>Type: {doc.licence_type} · Valid: {doc.licence_valid_from} – {doc.licence_valid_to} · First issued: {doc.licence_first_issue_date}</div>}
                      {doc.section_comment && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}><em>Comment:</em> {doc.section_comment}</div>}
                    </div>
                  ))}
                  <div style={{ display: 'grid', gap: 10, maxWidth: 480 }}>
                    <Field label="Upload new file"><input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setDocField(docType.key, 'file', e.target.files[0] || null)} /></Field>
                    {docType.hasLicenceFields && (
                      <>
                        <Field label="Licence Type">
                          <select value={form.licence_type || ''} onChange={e => setDocField(docType.key, 'licence_type', e.target.value)}>
                            <option value="">— Select —</option>
                            {LICENCE_TYPES.map(lt => <option key={lt}>{lt}</option>)}
                          </select>
                        </Field>
                        <div style={gridTwo}>
                          <Field label="Valid From"><input type="date" value={form.licence_valid_from || ''} onChange={e => setDocField(docType.key, 'licence_valid_from', e.target.value)} /></Field>
                          <Field label="Valid To"><input type="date" value={form.licence_valid_to || ''} onChange={e => setDocField(docType.key, 'licence_valid_to', e.target.value)} /></Field>
                          <Field label="First Issue Date"><input type="date" value={form.licence_first_issue_date || ''} onChange={e => setDocField(docType.key, 'licence_first_issue_date', e.target.value)} /></Field>
                        </div>
                      </>
                    )}
                    {(isAdmin || isRegionalManager) && <Field label="Section comment (internal)"><textarea rows={2} value={form.section_comment || ''} onChange={e => setDocField(docType.key, 'section_comment', e.target.value)} /></Field>}
                    {docMsg[docType.key] && <p style={{ margin: 0, fontSize: 13, color: docMsg[docType.key] === 'Uploaded.' ? 'var(--green)' : 'var(--red)' }}>{docMsg[docType.key]}</p>}
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => handleDocUpload(docType.key)} disabled={docUploading[docType.key] || !form.file}>
                      {docUploading[docType.key] ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Section>

      {/* ── Interview Recordings ── */}
      <Section title="Interview Recordings">
        {recordings.length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 12 }}>No recordings uploaded yet.</p>}
        {recordings.map(rec => (
          <div key={rec.id} style={{ padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 13, flex: 1, minWidth: 0 }}>
              <span style={{ marginRight: 8 }}>{rec.recording_type === 'video' ? '🎥' : '📄'}</span>
              <strong>{rec.title}</strong>
              <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text3)' }}>
                {new Date(rec.created_at).toLocaleDateString()} · {rec.uploaded_by || 'Unknown'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={async () => {
                  const res = await fetch(`${API}/api/recordings/${rec.id}/stream`, { headers: { Authorization: `Bearer ${tok()}` } });
                  const blob = await res.blob();
                  window.open(URL.createObjectURL(blob), '_blank');
                }}>
                {rec.recording_type === 'video' ? 'Watch' : 'View'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={async () => {
                  const res = await fetch(`${API}/api/recordings/${rec.id}/download`, { headers: { Authorization: `Bearer ${tok()}` } });
                  const blob = await res.blob();
                  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = rec.filename; a.click();
                }}>Download</button>
              {(isAdmin || isRegionalManager) && (
                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteRecording(rec.id)}>Delete</button>
              )}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: '14px 16px', border: '1px dashed var(--border2)', borderRadius: 'var(--radius-sm)', display: 'grid', gap: 10, maxWidth: 480 }}>
          <p style={{ fontWeight: 500, fontSize: 13, margin: 0 }}>Upload Recording or Minutes</p>
          <Field label="Title"><input value={recTitle} onChange={e => setRecTitle(e.target.value)} placeholder="e.g. Second Interview — 21 Apr 2026" /></Field>
          <Field label="File (video or PDF/Word)">
            <input type="file" accept="video/*,.pdf,.doc,.docx" onChange={e => setRecFile(e.target.files[0] || null)} />
          </Field>
          {recMsg && <p style={{ margin: 0, fontSize: 13, color: recMsg === 'Uploaded.' ? 'var(--green)' : 'var(--red)' }}>{recMsg}</p>}
          <button type="button" className="btn btn-primary btn-sm" onClick={handleUploadRecording} disabled={recUploading || !recFile}>
            {recUploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </Section>

      {/* ── References ── */}
      <Section title="References">
        {(applicant.references || []).length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 12 }}>No references yet.</p>}
        {(applicant.references || []).map(ref => (
          <div key={ref.id} style={{ padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 13 }}>
              <strong>{ref.name}</strong>
              {ref.position && <span style={{ color: 'var(--text2)' }}> — {ref.position}</span>}
              {ref.company && <span style={{ color: 'var(--text2)' }}> at {ref.company}</span>}
              <span className={`badge ${ref.added_by_role === 'applicant' ? 'badge-blue' : 'badge-purple'}`} style={{ marginLeft: 8 }}>{ref.added_by_role === 'applicant' ? 'Applicant' : 'Admin'}</span>
              {ref.phone && <div style={{ marginTop: 2, color: 'var(--text2)' }}>📞 {ref.phone}</div>}
              {ref.email && <div style={{ color: 'var(--text2)' }}>✉ {ref.email}</div>}
              {ref.admin_note && isAdmin && <div style={{ marginTop: 4, padding: '3px 8px', background: 'var(--amber-bg)', color: 'var(--amber)', borderRadius: 4, fontSize: 12 }}>Note: {ref.admin_note}</div>}
            </div>
            {(isAdmin || isRegionalManager) && <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteRef(ref.id)}>Remove</button>}
          </div>
        ))}
        {isAdmin && (
          <div style={{ marginTop: 12, padding: '14px 16px', border: '1px dashed var(--border2)', borderRadius: 'var(--radius-sm)' }}>
            <p style={{ fontWeight: 500, fontSize: 13, marginBottom: 12 }}>Add Reference</p>
            <div style={gridTwo}>
              <Field label="Full Name *"><input value={refForm.name} onChange={e => setRefForm(p => ({ ...p, name: e.target.value }))} /></Field>
              <Field label="Company"><input value={refForm.company} onChange={e => setRefForm(p => ({ ...p, company: e.target.value }))} /></Field>
              <Field label="Position"><input value={refForm.position} onChange={e => setRefForm(p => ({ ...p, position: e.target.value }))} /></Field>
              <Field label="Relationship"><input value={refForm.relationship} onChange={e => setRefForm(p => ({ ...p, relationship: e.target.value }))} /></Field>
              <Field label="Phone"><input value={refForm.phone} onChange={e => setRefForm(p => ({ ...p, phone: e.target.value }))} /></Field>
              <Field label="Email"><input type="email" value={refForm.email} onChange={e => setRefForm(p => ({ ...p, email: e.target.value }))} /></Field>
            </div>
            <Field label="Internal Note">
              <textarea rows={2} value={refForm.admin_note} onChange={e => setRefForm(p => ({ ...p, admin_note: e.target.value }))} />
            </Field>
            {refMsg && <p style={{ color: refMsg === 'Reference added.' ? 'var(--green)' : 'var(--red)', fontSize: 13, marginBottom: 8 }}>{refMsg}</p>}
            <button type="button" className="btn btn-ghost" onClick={handleAddRef} disabled={refSaving}>{refSaving ? 'Adding…' : 'Add Reference'}</button>
          </div>
        )}
      </Section>


      {/* ── HPCSA & Compliance Tracking ── */}
      {(isAdmin || isRegionalManager) && (
        <Section title="HPCSA & Compliance Tracking">
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            HPCSA registration runs annually (e.g. April 2026 – March 2027). Set renewal dates below.
            Placed candidates will show a warning when compliance is expiring or expired.
          </p>

          {/* HPCSA expiry warning for placed candidates */}
          {applicant.application_review_status === 'Placed' && (() => {
            const today = new Date();
            const warnings = [];
            const checks = [
              { label: 'HPCSA', from: f('hpcsa_valid_from'), to: f('hpcsa_valid_to') },
              { label: 'CPD', from: f('cpd_valid_from'), to: f('cpd_valid_to') },
              { label: 'Crim Check', from: f('imei_valid_from'), to: f('imei_valid_to') },
            ];
            checks.forEach(({ label, to }) => {
              if (!to) return;
              const expiry = new Date(to);
              const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
              if (daysLeft < 0) warnings.push({ label, status: 'expired', daysLeft });
              else if (daysLeft <= 30) warnings.push({ label, status: 'expiring', daysLeft });
            });
            if (warnings.length === 0) return null;
            return (
              <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: 'var(--red-bg)', border: '1px solid rgba(224,92,92,0.3)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>⚠️ Compliance Action Required</div>
                {warnings.map(({ label, status, daysLeft }) => (
                  <div key={label} style={{ fontSize: 13, color: 'var(--red)', marginBottom: 4 }}>
                    {status === 'expired'
                      ? `✗ ${label} registration has EXPIRED — confirm candidate has renewed`
                      : `⚠ ${label} registration expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — remind candidate to renew`}
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>HPCSA Registration</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Valid From"><input type="date" style={iStyle} value={f('hpcsa_valid_from')} onChange={setF('hpcsa_valid_from')} /></Field>
                <Field label="Valid To"><input type="date" style={iStyle} value={f('hpcsa_valid_to')} onChange={setF('hpcsa_valid_to')} /></Field>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>CPD Certificate</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Valid From"><input type="date" style={iStyle} value={f('cpd_valid_from')} onChange={setF('cpd_valid_from')} /></Field>
                <Field label="Valid To"><input type="date" style={iStyle} value={f('cpd_valid_to')} onChange={setF('cpd_valid_to')} /></Field>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>Crim Check</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Valid From"><input type="date" style={iStyle} value={f('imei_valid_from')} onChange={setF('imei_valid_from')} /></Field>
                <Field label="Valid To"><input type="date" style={iStyle} value={f('imei_valid_to')} onChange={setF('imei_valid_to')} /></Field>
              </div>
            </div>
          </div>

          {/* Compliance confirmation for placed candidates */}
          {applicant.application_review_status === 'Placed' && applicant.compliance_confirmed_at && (
            <div style={{ padding: '10px 14px', background: 'var(--green-bg)', border: '1px solid rgba(76,175,133,0.3)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--green)', marginBottom: 12 }}>
              ✓ Compliance last confirmed on {new Date(applicant.compliance_confirmed_at).toLocaleDateString('en-ZA')}
              {applicant.compliance_confirmed_by && ` by ${applicant.compliance_confirmed_by}`}
            </div>
          )}

          <button type="button" className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Saving…' : 'Save Compliance Dates'}
          </button>
        </Section>
      )}

      {/* ── Assessment ── */}
      {isAdmin && (
        <Section title="Assessment">
          <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: applicant.compliance_complete ? 'var(--green-bg)' : 'var(--amber-bg)', border: `1px solid ${applicant.compliance_complete ? 'rgba(76,175,133,0.3)' : 'rgba(232,169,74,0.3)'}`, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>Compliance Check</div>
              <div style={{ fontSize: 12, color: applicant.compliance_complete ? 'var(--green)' : 'var(--amber)', marginTop: 2 }}>
                {applicant.compliance_complete ? `✓ Complete${applicant.compliance_completed_at ? ` — ${new Date(applicant.compliance_completed_at).toLocaleDateString()}` : ''}` : '✗ Not yet complete'}
              </div>
            </div>
            <button type="button" className={`btn btn-sm ${applicant.compliance_complete ? 'btn-danger' : 'btn-success'}`} onClick={handleToggleCompliance} disabled={complianceToggling}>
              {complianceToggling ? 'Updating…' : applicant.compliance_complete ? 'Mark Incomplete' : 'Mark Complete'}
            </button>
          </div>

          {applicant.compliance_complete && (
            <div style={{ marginBottom: 16 }}>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSendTest} disabled={sendingTest}>{sendingTest ? 'Generating…' : 'Generate Assessment Link'}</button>
              {testLink && (
                <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Assessment link (share with candidate):</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input readOnly value={testLink} onFocus={e => e.target.select()} style={{ fontSize: 12 }} />
                    <button type="button" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => navigator.clipboard.writeText(testLink)}>Copy</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {!applicant.compliance_complete && <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>Compliance must be complete before sending a test.</p>}

          {applicant.latest_test && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: 13 }}>
                <strong>Latest test:</strong>{' '}
                <span style={{ textTransform: 'capitalize', color: applicant.latest_test.status === 'marked' ? 'var(--green)' : 'var(--text2)' }}>{applicant.latest_test.status}</span>
                {applicant.latest_test.score != null && <span style={{ marginLeft: 8, fontWeight: 700, color: applicant.latest_test.score >= 60 ? 'var(--green)' : 'var(--red)' }}>{applicant.latest_test.score}%</span>}
              </div>
              {applicant.latest_test.status === 'submitted' && (
                <Link to={`/candidate/${id}/mark-test`} className="btn btn-primary btn-sm">Mark Test →</Link>
              )}
            </div>
          )}
          {assessMsg && <p style={{ marginTop: 10, fontSize: 13, color: 'var(--text2)' }}>{assessMsg}</p>}
        </Section>
      )}

      {/* ── Scorecard — admin and viewer ── */}
      {(isAdmin || isRegionalManager || isViewer || isProjectManager || isUnitManager || isAccountManager || isFinanceManager) && (
        <Section title="Scorecard">
          <ScorecardForm
            applicantId={Number(id)}
            authToken={tok()}
            isAdmin={isAdmin}
            testScore={applicant.latest_test?.status === 'marked' && applicant.latest_test?.score != null
              ? Math.round(applicant.latest_test.score / 10)
              : null}
          />
        </Section>
      )}

      {/* ── Placement & IMEI Modal ── */}
      {showPlacementModal && (
        <Modal title={pendingStatus === 'Short listed' ? 'Send Crim Check Form' : 'Offer Accepted — Placement Details'} onClose={() => { setShowPlacementModal(false); setPendingStatus(''); setPlacementMsg(''); setImeiFormSent(false); }}>
          {pendingStatus === 'Short listed' ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Confirm that the Crim Check form has been sent to the candidate.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: imeiFormSent ? 'var(--green-bg)' : 'var(--bg3)', border: `1px solid ${imeiFormSent ? 'rgba(76,175,133,0.4)' : 'var(--border2)'}`, borderRadius: 'var(--radius-sm)', marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={imeiFormSent} onChange={e => setImeiFormSent(e.target.checked)} style={{ width: 'auto', flexShrink: 0 }} />
                <span style={{ color: imeiFormSent ? 'var(--green)' : 'var(--text)' }}>
                  {imeiFormSent ? '✓ Crim Check form sent to candidate' : 'I have sent the Crim Check form to the candidate'}
                </span>
              </label>
              {placementMsg && <p style={{ marginTop: 10, fontSize: 13, color: 'var(--red)' }}>{placementMsg}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => { setShowPlacementModal(false); setPendingStatus(''); setPlacementMsg(''); setImeiFormSent(false); }}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={async () => {
                  try {
                    setPlacementSaving(true);
                    const res = await fetch(`${API}/api/admin/applicants/${id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                      body: JSON.stringify({ application_review_status: 'Short listed' }),
                    });
                    const d = await res.json();
                    if (res.ok) {
                      setFields(p => ({ ...p, application_review_status: 'Short listed' }));
                      setShowPlacementModal(false);
                      setPendingStatus('');
                      setPlacementMsg('');
                      setImeiFormSent(false);
                    } else {
                      setPlacementMsg(d.error || 'Failed to update status');
                    }
                  } catch (e) {
                    setPlacementMsg(e.message);
                  } finally {
                    setPlacementSaving(false);
                  }
                }} disabled={placementSaving || !imeiFormSent}>
                  {placementSaving ? 'Saving…' : 'Save & Update Status'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Please fill in the placement details before saving the Offer Accepted status.
              </p>
              <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Fleet Number">
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={placementForm.fleet_nr} onChange={e => setPlacementForm(p => ({ ...p, fleet_nr: e.target.value }))} style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border2)' }}>
                  <option value="">Select or create FLT nr</option>
                  {fleets.map(f => <option key={f.id} value={f.fleet_nr}>{f.fleet_nr} {f.location_name ? `(${f.location_name})` : ''}</option>)}
                </select>
                <button type="button" onClick={() => setShowNewFleetModal(true)} style={{ padding: '8px 12px', borderRadius: 6, background: '#8b3db8', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>+ New</button>
              </div>
            </Field>
            <Field label="Project">
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={placementForm.project} onChange={e => setPlacementForm(p => ({ ...p, project: e.target.value }))} style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border2)' }}>
                  <option value="">Select or create Project</option>
                  {projects.map(p => <option key={p.id} value={p.project_name}>{p.project_name}</option>)}
                </select>
                <button type="button" onClick={() => setShowNewProjectModal(true)} style={{ padding: '8px 12px', borderRadius: 6, background: '#8b3db8', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>+ New</button>
              </div>
            </Field>
            <Field label="Area / Branch"><input value={placementForm.area} onChange={e => setPlacementForm(p => ({ ...p, area: e.target.value }))} placeholder="e.g. Sandton" /></Field>
            <div style={gridTwo}>
              <Field label="Manager Name"><input value={placementForm.manager_name} onChange={e => setPlacementForm(p => ({ ...p, manager_name: e.target.value }))} /></Field>
              <Field label="Manager Surname"><input value={placementForm.manager_surname} onChange={e => setPlacementForm(p => ({ ...p, manager_surname: e.target.value }))} /></Field>
            </div>
            <Field label="Manager Contact (phone / email)"><input value={placementForm.manager_contact} onChange={e => setPlacementForm(p => ({ ...p, manager_contact: e.target.value }))} /></Field>
          </div>
          {placementMsg && <p style={{ marginTop: 10, fontSize: 13, color: 'var(--red)' }}>{placementMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowPlacementModal(false); setPendingStatus(''); setPlacementMsg(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSavePlacement} disabled={placementSaving}>
              {placementSaving ? 'Saving…' : 'Save & Set Status'}
            </button>
          </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Offer Letter Modal ── */}
      {showOfferLetterModal && (
        <Modal title="Generate Offer Letter" onClose={() => { setShowOfferLetterModal(false); setOfferLetterMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates a PDF offer letter (Botha and Botha Inc template) and emails it to the
            candidate, with an optional CC. Please fill in every field carefully — the remuneration
            entered here must be confirmed before it can be sent.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Service Provided (role)">
              <input value={offerLetterForm.service_provided} onChange={e => setOfferLetterForm(p => ({ ...p, service_provided: e.target.value }))} placeholder="e.g. Optical Dispenser" />
            </Field>
            <Field label="Location">
              <input value={offerLetterForm.location} onChange={e => setOfferLetterForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Cape Town" />
            </Field>
            <Field label="Start Date">
              <input value={offerLetterForm.start_date} onChange={e => setOfferLetterForm(p => ({ ...p, start_date: e.target.value }))} placeholder="e.g. 1 September 2026" />
            </Field>
            <Field label="Remuneration (gross)">
              <input value={offerLetterForm.remuneration} onChange={e => { setOfferLetterForm(p => ({ ...p, remuneration: e.target.value })); setSalaryConfirmed(false); }} placeholder="e.g. R25,000" />
            </Field>
            <Field label="Reporting To">
              <input value={offerLetterForm.reporting_to} onChange={e => setOfferLetterForm(p => ({ ...p, reporting_to: e.target.value }))} placeholder="e.g. Jane Smith" />
            </Field>
            <Field label="CC Email (optional)">
              <input type="email" value={offerLetterForm.cc_email} onChange={e => setOfferLetterForm(p => ({ ...p, cc_email: e.target.value }))} placeholder="e.g. hr@guudvision.health" />
            </Field>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: salaryConfirmed ? 'var(--green-bg)' : 'var(--bg3)', border: `1px solid ${salaryConfirmed ? 'rgba(76,175,133,0.4)' : 'var(--border2)'}`, borderRadius: 'var(--radius-sm)', margin: '16px 0', cursor: offerLetterForm.remuneration ? 'pointer' : 'not-allowed', fontSize: 13, opacity: offerLetterForm.remuneration ? 1 : 0.6 }}>
            <input type="checkbox" checked={salaryConfirmed} disabled={!offerLetterForm.remuneration} onChange={e => setSalaryConfirmed(e.target.checked)} style={{ width: 'auto', flexShrink: 0 }} />
            <span style={{ color: salaryConfirmed ? 'var(--green)' : 'var(--text)' }}>
              {salaryConfirmed
                ? `✓ I confirm the remuneration "${offerLetterForm.remuneration}" is correct`
                : 'I confirm the remuneration entered above is correct'}
            </span>
          </label>

          {offerLetterMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{offerLetterMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowOfferLetterModal(false); setOfferLetterMsg(''); }}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendOfferLetter}
              disabled={offerLetterSaving || !salaryConfirmed || !offerLetterForm.remuneration}
            >
              {offerLetterSaving ? 'Sending…' : '📧 Send Offer Letter'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Employment Contract Modal ── */}
      {showContractModal && (
        <Modal title="Generate Employment Contract" onClose={() => { setShowContractModal(false); setContractMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates the Fixed-Term Employment Contract (Botha and Botha Incorporated template)
            and emails the candidate a secure link to review and sign it online. Fields already on the
            candidate's profile have been filled in automatically — please check them and fill in the rest.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="ID / Passport Number">
              <input value={contractForm.id_number} onChange={e => setContractForm(p => ({ ...p, id_number: e.target.value }))} placeholder="e.g. 9001015800086" />
            </Field>
            <Field label="HPCSA Number">
              <input value={contractForm.hpcsa_number} onChange={e => setContractForm(p => ({ ...p, hpcsa_number: e.target.value }))} placeholder="e.g. 0123456" />
            </Field>
            <Field label="Residential Address">
              <input value={contractForm.residential_address} onChange={e => setContractForm(p => ({ ...p, residential_address: e.target.value }))} placeholder="Street, City, Province, Postal Code" />
            </Field>
            <Field label="Position">
              <select value={contractForm.position} onChange={e => setContractForm(p => ({ ...p, position: e.target.value }))} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border2)' }}>
                <option value="">— Select —</option>
                <option value="optometrist">Optometrist</option>
                <option value="dispenser">Optical Dispenser</option>
              </select>
            </Field>
            <Field label="Project / Engagement Name">
              <input value={contractForm.project_name} onChange={e => setContractForm(p => ({ ...p, project_name: e.target.value }))} placeholder="e.g. SIOC Mobile Eye Care Project" />
            </Field>
            <Field label="Reporting Manager">
              <input value={contractForm.reporting_manager} onChange={e => setContractForm(p => ({ ...p, reporting_manager: e.target.value }))} placeholder="e.g. John Manager" />
            </Field>
            <Field label="Primary Place of Work">
              <input value={contractForm.primary_place_of_work} onChange={e => setContractForm(p => ({ ...p, primary_place_of_work: e.target.value }))} placeholder="e.g. GM 4 — Muldersdrift" />
            </Field>
            <div style={gridTwo}>
              <Field label="Commencement Date">
                <input
                  type="date"
                  value={contractForm.commencement_date}
                  onChange={e => setContractForm(p => ({
                    ...p,
                    commencement_date: e.target.value,
                    fixed_term_end_date: p.probation_months ? addMonthsToISODate(e.target.value, p.probation_months) : p.fixed_term_end_date,
                  }))}
                />
              </Field>
              <Field label="Probation Period (months)">
                <input
                  type="number" min={1}
                  value={contractForm.probation_months}
                  onChange={e => setContractForm(p => ({
                    ...p,
                    probation_months: e.target.value,
                    fixed_term_end_date: p.commencement_date ? addMonthsToISODate(p.commencement_date, e.target.value) : p.fixed_term_end_date,
                  }))}
                />
              </Field>
            </div>
            <Field label="Fixed Term End Date (calculated from the above — you can also type a different date directly)">
              <input type="date" value={contractForm.fixed_term_end_date} onChange={e => setContractForm(p => ({ ...p, fixed_term_end_date: e.target.value }))} />
            </Field>
            <Field label="Gross Salary">
              <input value={contractForm.gross_salary} onChange={e => { setContractForm(p => ({ ...p, gross_salary: e.target.value })); setContractConfirmed(false); }} placeholder="e.g. R25,000" />
            </Field>
            <Field label="CC (optional — e.g. HR, the candidate's manager)">
              <div style={{ display: 'grid', gap: 8 }}>
                {(() => {
                  const parts = contractForm.cc_email.split(',').map(s => s.trim());
                  const slots = [0, 1, 2].map(i => parts[i] || '');
                  const placeholders = ['e.g. hr@guudvision.health', 'e.g. manager@guudvision.health', 'another address (optional)'];
                  return slots.map((val, i) => (
                    <input key={i} type="email" value={val} placeholder={placeholders[i]}
                      onChange={e => {
                        const updated = [...slots];
                        updated[i] = e.target.value;
                        setContractForm(p => ({ ...p, cc_email: updated.filter(Boolean).join(', ') }));
                      }} />
                  ));
                })()}
              </div>
            </Field>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: contractConfirmed ? 'var(--green-bg)' : 'var(--bg3)', border: `1px solid ${contractConfirmed ? 'rgba(76,175,133,0.4)' : 'var(--border2)'}`, borderRadius: 'var(--radius-sm)', margin: '16px 0', cursor: contractForm.gross_salary ? 'pointer' : 'not-allowed', fontSize: 13, opacity: contractForm.gross_salary ? 1 : 0.6 }}>
            <input type="checkbox" checked={contractConfirmed} disabled={!contractForm.gross_salary} onChange={e => setContractConfirmed(e.target.checked)} style={{ width: 'auto', flexShrink: 0 }} />
            <span style={{ color: contractConfirmed ? 'var(--green)' : 'var(--text)' }}>
              {contractConfirmed
                ? `✓ I confirm the salary "${contractForm.gross_salary}" and all details above are correct`
                : 'I confirm the salary and all details above are correct'}
            </span>
          </label>

          {contractMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{contractMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowContractModal(false); setContractMsg(''); }}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendContract}
              disabled={
                contractSaving || !contractConfirmed || !contractForm.gross_salary || !contractForm.position ||
                !contractForm.commencement_date || !contractForm.fixed_term_end_date
              }
            >
              {contractSaving ? 'Sending…' : '📧 Send Contract for Signature'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Contract Addendum Modal ── */}
      {showAddendumModal && (
        <Modal title="Extend Contract" onClose={() => { setShowAddendumModal(false); setAddendumMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates a Contract Addendum (same letterhead as the original contract) extending the
            current fixed term end date ({employmentContracts[0]?.fixed_term_end_date || 'not on file'}),
            and emails the candidate a secure link to review and sign it online. Once signed, the contract's
            end date updates automatically.
            {addendumForm.effective_date && addendumForm.extension_months > 0 && (
              <> New end date will be <b>{addendumNewEndDatePreview}</b>.</>
            )}
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Effective Date">
              <input type="date" value={addendumForm.effective_date} onChange={e => setAddendumForm(p => ({ ...p, effective_date: e.target.value }))} />
            </Field>
            <Field label="Extension Length (months)">
              <input type="number" min={1} value={addendumForm.extension_months} onChange={e => setAddendumForm(p => ({ ...p, extension_months: e.target.value }))} />
            </Field>
            <Field label="Location of Work">
              <input type="text" value={addendumForm.location_of_work} onChange={e => setAddendumForm(p => ({ ...p, location_of_work: e.target.value }))} placeholder="e.g. Cape Town" />
            </Field>
            <Field label="Reason (optional, internal note — not printed on the addendum)">
              <textarea value={addendumForm.reason} onChange={e => setAddendumForm(p => ({ ...p, reason: e.target.value }))} rows={2} placeholder="e.g. project extended by client" />
            </Field>
            <Field label="CC (optional — e.g. HR, the candidate's manager)">
              <div style={{ display: 'grid', gap: 8 }}>
                {(() => {
                  const parts = addendumForm.cc_email.split(',').map(s => s.trim());
                  const slots = [0, 1, 2].map(i => parts[i] || '');
                  const placeholders = ['e.g. hr@guudvision.health', 'e.g. manager@guudvision.health', 'another address (optional)'];
                  return slots.map((val, i) => (
                    <input key={i} type="email" value={val} placeholder={placeholders[i]}
                      onChange={e => {
                        const updated = [...slots];
                        updated[i] = e.target.value;
                        setAddendumForm(p => ({ ...p, cc_email: updated.filter(Boolean).join(', ') }));
                      }} />
                  ));
                })()}
              </div>
            </Field>
          </div>

          {addendumMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{addendumMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowAddendumModal(false); setAddendumMsg(''); }}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendAddendum}
              disabled={addendumSaving || !addendumForm.effective_date || !addendumForm.extension_months}
            >
              {addendumSaving ? 'Sending…' : '📧 Send Addendum for Signature'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Criminal Check Consent Modal ── */}
      {showCriminalCheckModal && (
        <Modal title="Send Criminal Check Consent" onClose={() => { setShowCriminalCheckModal(false); setCriminalCheckMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates the Background Screening Request Consent Form and emails the candidate a
            secure link to review, answer the previous-convictions questions, and sign online.
          </p>
          <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Checks to be performed</p>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16, maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
            {CRIMINAL_CHECK_OPTIONS.map(opt => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={criminalCheckForm.checks_selected.includes(opt)}
                  onChange={e => setCriminalCheckForm(p => ({
                    ...p,
                    checks_selected: e.target.checked ? [...p.checks_selected, opt] : p.checks_selected.filter(c => c !== opt),
                  }))}
                />
                {opt}
              </label>
            ))}
          </div>
          <Field label="CC Email (optional)">
            <input type="email" value={criminalCheckForm.cc_email} onChange={e => setCriminalCheckForm(p => ({ ...p, cc_email: e.target.value }))} placeholder="e.g. hr@guudvision.health" />
          </Field>
          {criminalCheckMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{criminalCheckMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowCriminalCheckModal(false); setCriminalCheckMsg(''); }}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendCriminalCheck}
              disabled={criminalCheckSaving || criminalCheckForm.checks_selected.length === 0}
            >
              {criminalCheckSaving ? 'Sending…' : '📧 Send Criminal Check Consent'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Pledge of Confidentiality Modal ── */}
      {showPledgeModal && (
        <Modal title="Send Pledge of Confidentiality" onClose={() => { setShowPledgeModal(false); setPledgeMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates the Pledge of Confidentiality and emails the candidate a secure link to
            review and sign online.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="CC Email (optional)">
              <input type="email" value={pledgeForm.cc_email} onChange={e => setPledgeForm(p => ({ ...p, cc_email: e.target.value }))} placeholder="e.g. hr@guudvision.health" />
            </Field>
          </div>
          {pledgeMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{pledgeMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowPledgeModal(false); setPledgeMsg(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSendPledge} disabled={pledgeSaving}>
              {pledgeSaving ? 'Sending…' : '📧 Send Pledge of Confidentiality'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Cellphone Policy Modal ── */}
      {showCellphoneModal && (
        <Modal title="Send Cellphone Policy" onClose={() => { setShowCellphoneModal(false); setCellphoneMsg(''); }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This generates the Company Cell Phone Policy acknowledgment and emails the candidate a
            secure link to review and sign online.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="CC Email (optional)">
              <input type="email" value={cellphoneForm.cc_email} onChange={e => setCellphoneForm(p => ({ ...p, cc_email: e.target.value }))} placeholder="e.g. hr@guudvision.health" />
            </Field>
          </div>
          {cellphoneMsg && <p style={{ marginTop: 6, fontSize: 13, color: 'var(--red)' }}>{cellphoneMsg}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowCellphoneModal(false); setCellphoneMsg(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSendCellphone} disabled={cellphoneSaving}>
              {cellphoneSaving ? 'Sending…' : '📧 Send Cellphone Policy'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── New Fleet Modal ── */}
      {showNewFleetModal && (
        <Modal title="Create New Fleet" onClose={() => { setShowNewFleetModal(false); setNewFleetForm({ fleet_nr: '', location_name: '' }); }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Fleet Number (e.g. FLT104)"><input value={newFleetForm.fleet_nr} onChange={e => setNewFleetForm(p => ({ ...p, fleet_nr: e.target.value }))} placeholder="FLT104" /></Field>
            <Field label="Location Name (e.g. GM1)"><input value={newFleetForm.location_name} onChange={e => setNewFleetForm(p => ({ ...p, location_name: e.target.value }))} placeholder="GM1" /></Field>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowNewFleetModal(false); setNewFleetForm({ fleet_nr: '', location_name: '' }); }}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={async () => {
              try {
                const res = await fetch(`${API}/api/admin/fleet`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                  body: JSON.stringify(newFleetForm),
                });
                const d = await res.json();
                if (res.ok) {
                  setFleets([...fleets, d.fleet]);
                  setPlacementForm(p => ({ ...p, fleet_nr: d.fleet.fleet_nr }));
                  setShowNewFleetModal(false);
                  setNewFleetForm({ fleet_nr: '', location_name: '' });
                } else {
                  alert(d.error || 'Failed to create fleet');
                }
              } catch (e) {
                alert('Error: ' + e.message);
              }
            }}>Create Fleet</button>
          </div>
        </Modal>
      )}

      {/* ── New Project Modal ── */}
      {showNewProjectModal && (
        <Modal title="Create New Project" onClose={() => { setShowNewProjectModal(false); setNewProjectForm({ project_name: '' }); }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Project Name (e.g. SIOC, RAF, RMA)"><input value={newProjectForm.project_name} onChange={e => setNewProjectForm(p => ({ ...p, project_name: e.target.value }))} placeholder="SIOC" /></Field>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowNewProjectModal(false); setNewProjectForm({ project_name: '' }); }}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={async () => {
              try {
                const res = await fetch(`${API}/api/admin/projects`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                  body: JSON.stringify(newProjectForm),
                });
                const d = await res.json();
                if (res.ok) {
                  setProjects([...projects, d.project]);
                  setPlacementForm(p => ({ ...p, project: d.project.project_name }));
                  setShowNewProjectModal(false);
                  setNewProjectForm({ project_name: '' });
                } else {
                  alert(d.error || 'Failed to create project');
                }
              } catch (e) {
                alert('Error: ' + e.message);
              }
            }}>Create Project</button>
          </div>
        </Modal>
      )}

      {/* ── Second Interview Modal ── */}
      {showSecondInterview && (
        <Modal title="Request 2nd Interview" onClose={() => setShowSecondInterview(false)}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            Copy the email address below to send a second interview invitation to the candidate.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
            <span style={{ fontSize: 16 }}>📧</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
              {applicant.user?.email || 'No email on file'}
            </span>
            {applicant.user?.email && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={async () => {
                  navigator.clipboard.writeText(applicant.user.email);
                  setEmailCopied(true);
                  // Append a timestamped note to admin_notes
                  const stamp = new Date().toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
                  const note = `[${stamp}] 2nd interview request sent to ${applicant.user.email}.`;
                  const updated = adminNotes ? `${adminNotes}\n${note}` : note;
                  setAdminNotes(updated);
                  await fetch(`${API}/api/admin/applicants/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                    body: JSON.stringify({ admin_notes: updated }),
                  });
                }}
              >
                {emailCopied ? '✓ Copied & Noted' : 'Copy'}
              </button>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>
            Paste this address into your email client to send the 2nd interview invitation.
          </p>
        </Modal>
      )}

      {/* ── Delete Document Confirmation Modal ── */}
      {docToDelete && (
        <Modal title="Remove Document" onClose={() => setDocToDelete(null)}>
          <p style={{ fontSize: 14, marginBottom: 20 }}>
            Are you sure you want to remove <strong>{docToDelete.filename}</strong>? This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDocToDelete(null)}>Cancel</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleDocDelete}>Confirm Remove</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
